import { invariantResponse } from '@epic-web/invariant'
import {
	type ActionFunctionArgs,
	json,
	redirect,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { Form, Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import { Spacer } from '#app/components/spacer'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { prisma } from '#app/utils/db.server'
import { getAuthUrl } from '#app/utils/google-calendar'
import { getUserImgSrc } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user'

export const action = async ({ request }: ActionFunctionArgs) => {
	const formData = await request.formData()
	const action = formData.get('_action')

	if (action === 'sync') {
		const authUrl = getAuthUrl()
		return redirect(authUrl)
	}
}

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			createdAt: true,
			image: { select: { id: true } },
			companies: {
				select: {
					company: {
						select: {
							id: true,
							name: true,
						}
					}
				}
			},
			createdRecipes: {
				select: {
					id: true,
				}
			},
			ownedCompanies: {
				select: {
					id: true,
					name: true,
				}
			}
		},
		where: {
			username: params.username,
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	// Get the primary company - either the first owned company or the first company they're a member of
	const primaryCompany = user.ownedCompanies.length > 0 
		? user.ownedCompanies[0] 
		: user.companies.length > 0 
			? user.companies[0].company 
			: null

	// Count of recipes created by the user
	const recipeCount = user.createdRecipes.length

	return json({ 
		user, 
		userJoinedDisplay: user.createdAt.toLocaleDateString(),
		primaryCompany,
		recipeCount
	})
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const user = data.user
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = data.user.id === loggedInUser?.id

	return (
		<div className="container mb-48 mt-36 flex flex-col items-center justify-center">
			<Spacer size="4xs" />

			<div className="container flex flex-col items-center rounded-3xl bg-muted p-12">
				<div className="relative w-52">
					<div className="absolute -top-40">
						<div className="relative">
							<img
								src={getUserImgSrc(data.user.image?.id)}
								alt={userDisplayName}
								className="h-52 w-52 rounded-full object-cover"
							/>
						</div>
					</div>
				</div>

				<Spacer size="sm" />

				<div className="flex flex-col items-center">
					<div className="flex flex-wrap items-center justify-center gap-4">
						<h1 className="text-center text-h2">{userDisplayName}</h1>
					</div>
					<p className="mt-2 text-center text-muted-foreground">
						Joined {data.userJoinedDisplay}
					</p>
					
					{/* Company and Recipe information */}
					{data.primaryCompany ? (
						<p className="mt-2 text-center">
							Company: <span className="font-medium">{data.primaryCompany.name}</span>
						</p>
					) : null}
					
					<p className="mt-2 text-center">
						Recipes Created: <span className="font-medium">{data.recipeCount}</span>
					</p>
					
					{isLoggedInUser ? (
						<Form action="/logout" method="POST" className="mt-3">
							<Button type="submit" variant="link" size="pill">
								<Icon name="exit" className="scale-125 max-md:scale-150">
									Logout
								</Icon>
							</Button>
						</Form>
					) : null}
					<div className="mt-10 flex gap-4">
						{isLoggedInUser ? (
							<>
								<Button asChild>
									<Link to="/settings/company" prefetch="intent">
										Edit company profile
									</Link>
								</Button>
								<Button asChild>
									<Link to="/settings/profile" prefetch="intent">
										Edit profile
									</Link>
								</Button>
								{data.recipeCount > 0 && (
									<Button asChild>
										<Link to="/recipes" prefetch="intent">
											View my recipes
										</Link>
									</Button>
								)}
							</>
						) : (
							<Button asChild>
								{/* <Link to="notes" prefetch="intent">
									{userDisplayName}'s notes
								</Link> */}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | Oui Chef` },
		{
			name: 'description',
			content: `Profile of ${displayName} on Oui Chef`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}